import * as React from 'react';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { ExpandMore, OpenInNew } from '@material-ui/icons';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(1, 0),
  },

  /* ---- Page header ---- */
  pageHeader: {
    marginBottom: theme.spacing(2.5),
  },
  pageTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    maxWidth: 560,
    lineHeight: 1.6,
  },
  headerDivider: {
    margin: theme.spacing(2.5, 0, 2),
  },

  /* ---- List ---- */
  brandList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },

  /* ---- Accordion item (card-like container) ---- */
  item: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 5,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    transition: 'box-shadow 200ms ease, border-color 200ms ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(44,6,3,0.10)',
    },
  },
  itemOpen: {
    boxShadow: '0 4px 12px rgba(44,6,3,0.12)',
    borderColor: 'transparent',
  },

  /* ---- Collapsed summary ---- */
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 2.5),
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left' as const,
    background: 'none',
    border: 'none',
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  summaryContent: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  brandName: {
    fontWeight: 600,
    fontSize: '1.05rem',
    lineHeight: 1.3,
  },
  chip: {
    fontSize: '0.66rem',
    height: 20,
    fontWeight: 600,
    letterSpacing: '0.3px',
  },
  tagline: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
    lineHeight: 1.4,
  },
  summaryActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    marginLeft: theme.spacing(2),
  },
  externalLinkButton: {
    opacity: 0.4,
    transition: 'opacity 150ms ease',
    '&:hover': {
      opacity: 1,
    },
  },
  expandButton: {
    transition: 'transform 250ms ease',
  },
  expandButtonOpen: {
    transform: 'rotate(180deg)',
  },

  /* ---- Expanded detail ---- */
  detailWrapper: {
    padding: theme.spacing(0, 2.5, 2.5),
  },
  detailDivider: {
    marginBottom: theme.spacing(2),
  },
  detailBody: {
    display: 'flex',
    gap: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  description: {
    flex: '1 1 55%',
    fontSize: '0.86rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.75,
  },
  pricingSection: {
    flex: '1 1 40%',
  },
  pricingHeader: {
    fontWeight: 600,
    fontSize: '0.72rem',
    letterSpacing: '0.6px',
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(1.5),
  },
  pricingGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1),
  },
  pricingTile: {
    flex: '1 1 95px',
    minWidth: 85,
    borderRadius: 5,
    padding: theme.spacing(1.25),
    textAlign: 'center' as const,
  },
  pricingTileLabel: {
    fontSize: '0.68rem',
    fontWeight: 600,
    opacity: 0.8,
    marginBottom: 2,
  },
  pricingTileValue: {
    fontSize: '1.05rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  pricingTileSub: {
    fontSize: '0.63rem',
    opacity: 0.65,
    marginTop: 2,
  },
  pricingNote: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.5),
    lineHeight: 1.55,
    fontStyle: 'italic',
  },
  quoteLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.8rem',
    fontWeight: 600,
    textDecoration: 'none',
    marginTop: theme.spacing(1.5),
    transition: 'opacity 150ms ease',
    '&:hover': {
      opacity: 0.75,
    },
  },
}));

// ---------------------------------------------------------------------------
// Types & data
// ---------------------------------------------------------------------------

interface PricingTier {
  label: string;
  value: string;
  sub?: string;
}

interface Brand {
  name: string;
  tagline: string;
  description: string;
  category: string;
  accentColor: string;
  chipBg: string;
  url: string;
  pricing?: {
    tiers?: PricingTier[];
    note?: string;
    quoteUrl?: string;
    quoteLabel?: string;
  };
}

const brands: Brand[] = [
  {
    name: 'Konnekt',
    tagline: "Malta's largest recruitment agency",
    description:
      "Founded in 2007, Konnekt is Malta's leading recruitment agency specialising in matching candidates with employers across finance, tech, compliance, and engineering. Services include permanent search & selection, interim and temporary staffing, executive search, and secondments - covering the full recruitment lifecycle from advertising and screening through to offer negotiation and post-placement follow-up.",
    category: 'Recruitment',
    accentColor: '#65B9E8',
    chipBg: '#E1F5FE',
    url: 'https://konnekt.com',
    pricing: {
      note: "Recruitment fees are based on a percentage of the hired candidate's salary.",
    },
  },
  {
    name: 'jobsinmalta.com',
    tagline: "Malta's job vacancy network",
    description:
      "An integrated job board designed for the Maltese market while also supporting opportunities beyond the local market. Employers post vacancies directly and jobseekers can browse, apply, and set up alerts. One of Malta's most visited employment platforms and a self-service channel for companies that prefer to recruit independently.",
    category: 'Job Board',
    accentColor: '#004270',
    chipBg: '#E0E9F0',
    url: 'https://jobsinmalta.com',
    pricing: {
      tiers: [
        { label: '1 Credit', value: '€95', sub: 'per listing' },
        { label: '5 Credits', value: '€80', sub: 'per listing · €400' },
        { label: '10 Credits', value: '€70', sub: 'per listing · €700' },
        { label: 'Unlimited', value: 'Custom', sub: '12-month plan' },
      ],
      note: 'Each listing is live for up to 30 days. All plans include targeted email notifications, Google Ads & social media exposure, and the free ATS. NGOs get unlimited free listings.',
    },
  },
  {
    name: 'Talexio',
    tagline: 'Cloud-based HR & payroll software',
    description:
      'A SaaS platform covering the full HR lifecycle: employee records, Malta-compliant payroll, time & attendance with geo-fencing, applicant tracking, onboarding checklists, training management, and performance reviews. Scalable from small businesses to large enterprises.',
    category: 'HR Software',
    accentColor: '#FD6A68',
    chipBg: '#FFF1F1',
    url: 'https://talexio.com',
    pricing: {
      quoteUrl: 'https://talexio.com/pricing/',
      quoteLabel: 'Get a quote on talexio.com',
    },
  },
  {
    name: 'KonnektPayroll',
    tagline: 'Managed payroll outsourcing for Malta',
    description:
      'A dedicated payroll outsourcing service. Companies hand off their entire payroll cycle - salary calculations, FSS submissions, bank transfers, and electronic payslips. Also offers Employer of Record (EOR) services for foreign companies hiring in Malta without a local entity, plus work permit applications and renewals. ISO 27001 certified.',
    category: 'Payroll Services',
    accentColor: '#16466e',
    chipBg: '#E8EDF1',
    url: 'https://konnektpayroll.com',
    pricing: {
      quoteUrl: 'https://konnektpayroll.com/payroll-calculator/',
      quoteLabel: 'Use the payroll calculator',
    },
  },
  {
    name: 'Salariesinmalta.com',
    tagline: "Malta's salary benchmarking platform",
    description:
      'An online compensation benchmarking tool built on real, validated salary data submitted by companies and individuals across Malta. HR teams and employers can compare pay across roles, industries, and company sizes. Data includes gross salary ranges, variable pay, benefits, and demographic breakdowns.',
    category: 'Salary Data',
    accentColor: '#41B9BC',
    chipBg: '#E0F2F2',
    url: 'https://salariesinmalta.com',
    pricing: {
      tiers: [
        { label: 'Free', value: '€0', sub: '1 user · median data' },
        { label: '1–19 emp', value: '€75', sub: 'per year' },
        { label: '20–79 emp', value: '€150', sub: 'per year' },
        { label: '80–150 emp', value: '€250', sub: 'per year' },
        { label: '150+ emp', value: '€350', sub: 'per year' },
      ],
      note: 'Free plan shows median salary ranges. Premium unlocks full distribution (min → max), demographic breakdowns, industry filtering, and unlimited users. Company salary data submission required for both plans.',
    },
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BrandsView: React.FC = () => {
  const classes = useStyles();
  const [openIdx, setOpenIdx] = React.useState<number | null>(null);

  const toggle = (idx: number) => {
    setOpenIdx((prev) => (prev === idx ? null : idx));
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.pageHeader}>
        <Typography
          variant="h5"
          component={'h1' as React.ElementType}
          className={classes.pageTitle}
        >
          Our Brands
        </Typography>
        <Typography variant="body2" className={classes.pageSubtitle}>
          Quick reference for the KonnektTalexio family. Click any brand for
          details and pricing.
        </Typography>
      </Box>

      <Divider className={classes.headerDivider} />

      {/* Brand list */}
      <Box className={classes.brandList}>
        {brands.map((brand, idx) => {
          const isOpen = openIdx === idx;
          const panelId = `brand-panel-${idx}`;
          const headerId = `brand-header-${idx}`;

          return (
            <Box
              key={brand.name}
              className={`${classes.item} ${isOpen ? classes.itemOpen : ''}`}
            >
              {/* Summary row — keyboard-accessible accordion trigger */}
              <Box
                id={headerId}
                className={classes.summaryRow}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                aria-controls={panelId}
                aria-label={`${brand.name} — ${
                  isOpen ? 'collapse' : 'expand'
                } details`}
                onClick={() => toggle(idx)}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(idx);
                  }
                }}
              >
                <Box className={classes.summaryContent}>
                  <Box className={classes.nameRow}>
                    <Typography
                      className={classes.brandName}
                      style={{ color: brand.accentColor }}
                    >
                      {brand.name}
                    </Typography>
                    <Chip
                      label={brand.category}
                      className={classes.chip}
                      style={{
                        backgroundColor: brand.chipBg,
                        color: brand.accentColor,
                      }}
                    />
                  </Box>
                  <Typography className={classes.tagline}>
                    {brand.tagline}
                  </Typography>
                </Box>

                <Box className={classes.summaryActions}>
                  <Tooltip title={`Visit ${brand.name}`} placement="top">
                    <IconButton
                      className={classes.externalLinkButton}
                      size="small"
                      href={brand.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                      aria-label={`Visit ${brand.name}`}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <OpenInNew style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    className={`${classes.expandButton} ${
                      isOpen ? classes.expandButtonOpen : ''
                    }`}
                    size="small"
                    aria-hidden={true}
                    tabIndex={-1}
                  >
                    <ExpandMore style={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Expanded detail */}
              <Collapse in={isOpen} timeout={250} unmountOnExit>
                <Box
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  className={classes.detailWrapper}
                >
                  <Divider className={classes.detailDivider} />
                  <Box className={classes.detailBody}>
                    <Typography className={classes.description}>
                      {brand.description}
                    </Typography>

                    {brand.pricing && (
                      <Box className={classes.pricingSection}>
                        <Typography
                          className={classes.pricingHeader}
                          style={{ color: brand.accentColor }}
                        >
                          Pricing
                        </Typography>

                        {brand.pricing.tiers && (
                          <Box className={classes.pricingGrid}>
                            {brand.pricing.tiers.map((tier) => (
                              <Box
                                key={tier.label}
                                className={classes.pricingTile}
                                style={{
                                  backgroundColor: brand.chipBg,
                                }}
                              >
                                <Typography
                                  className={classes.pricingTileLabel}
                                  style={{
                                    color: brand.accentColor,
                                  }}
                                >
                                  {tier.label}
                                </Typography>
                                <Typography
                                  className={classes.pricingTileValue}
                                  style={{
                                    color: brand.accentColor,
                                  }}
                                >
                                  {tier.value}
                                </Typography>
                                {tier.sub && (
                                  <Typography
                                    className={classes.pricingTileSub}
                                  >
                                    {tier.sub}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}

                        {brand.pricing.note && (
                          <Typography className={classes.pricingNote}>
                            {brand.pricing.note}
                          </Typography>
                        )}

                        {brand.pricing.quoteUrl && (
                          <a
                            href={brand.pricing.quoteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.quoteLink}
                            style={{ color: brand.accentColor }}
                            onClick={(e: React.MouseEvent) =>
                              e.stopPropagation()
                            }
                          >
                            {brand.pricing.quoteLabel || 'Get a quote'}
                            <OpenInNew style={{ fontSize: 14 }} />
                          </a>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default BrandsView;
